/**
 * CORE-1285 — сколько длится материал на самом деле.
 *
 * `VideoPractices.DurationMinutes` на бэкенде — это `ceil(секунды / 60)`, то есть
 * ВСЁ, что короче минуты, хранится как «1». Библиотека комнаты печатала это число
 * как «1 мин», а комната режет практику по реальному концу медиа
 * (`computeEffectiveTotal`, CORE-757) — на двухсекундном клипе счётчик показывал
 * «0:02» и фаза уходила в «Практика завершена» раньше, чем зритель видел кадр.
 * Ровно этот разрыв — между обещанием карточки и файлом — и есть дефект: ведущему
 * не за что было зацепиться, чтобы понять, что материал короткий.
 *
 * Секунды приходят с бэкенда рядом с минутами (`durationSeconds`). Их может не
 * быть — у строки, до которой не дошёл бэкфилл, и у старой сборки бэкенда, — и
 * тогда мы честно возвращаемся к минутам: соврать округлением всё же лучше, чем
 * не показать ничего.
 */

/** Длина материала в секундах: точная, если бэкенд её знает; иначе — из минут. */
export function practiceLengthSeconds(
    durationSeconds: number | null | undefined,
    durationMinutes: number | null | undefined,
): number | null {
    if (typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0) {
        return Math.floor(durationSeconds);
    }
    if (typeof durationMinutes === 'number' && Number.isFinite(durationMinutes) && durationMinutes > 0) {
        return Math.round(durationMinutes * 60);
    }
    return null;
}

/**
 * Подпись длительности для карточки материала. Короче минуты — говорим секундами
 * («2 сек»), потому что «1 мин» здесь и было обещанием, которого файл не держит.
 * Минута и дольше — как раньше, целыми минутами (округляем к ближайшей, чтобы
 * 26:44 не стало «27 мин» на карточке при «26:44» на счётчике).
 *
 * Возвращает null, когда о длине не известно ничего: подпись тогда просто не
 * рисуется — это честнее выдуманных «10 мин».
 */
export function formatPracticeDuration(
    durationSeconds: number | null | undefined,
    durationMinutes: number | null | undefined,
): string | null {
    const seconds = practiceLengthSeconds(durationSeconds, durationMinutes);
    if (seconds === null) return null;
    if (seconds < 60) return `${seconds} сек`;
    return `${Math.max(1, Math.round(seconds / 60))} мин`;
}

/**
 * Каталог практик (`PracticesScreen`) — таймкод для материала короче минуты
 * («0:25»). Прод-FAIL 23.09: карточка печатала ceil-минуты как «1 min» на
 * роликах в 2, 25 и 54 секунды. От минуты и дольше — null: карточка остаётся
 * на своём формате минутами.
 */
export function formatShortPracticeTimecode(durationSeconds: number | null | undefined): string | null {
    const seconds = practiceLengthSeconds(durationSeconds, null);
    if (seconds === null || seconds >= 60) return null;
    return `0:${String(seconds).padStart(2, '0')}`;
}
